<script setup lang="ts">
import {reset} from "@formkit/core";
import SelectedCarsBatchInvoice from "./parts/SelectedCarsBatchInvoice.vue";
import {useModalOptions} from "~/composables/modal";
import { useDynamicFormSchema } from "~/composables/dynamicForms";
import { UiAccordion } from "#components"

interface CarRequestedServiceProps {
  formData?: any
  form?: object
  code?: string
  carId?: number
}

const library = markRaw({
  SelectedCarsBatchInvoice: SelectedCarsBatchInvoice,
  UiAccordion: UiAccordion,
})

const {
  carId,
  form,
  code = 'defaultcode',
  formData,
} = defineProps<CarRequestedServiceProps>();

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'close'): void
  (e: 'refresh'): void
}>()

// const {data: form, pending} = await useServiceForm({
//   carId: +carId.value,
//   code: code.value,
//   vinFrom: vinFrom.value,
// });

// const [vinCodeOptions] = useCarFieldAutocomplete({field: 'vinCode'});

const {t} = useI18n()

const getClient = () => {
  const client = useClient()
  return client
}

const onModalSubmit = async (formData, node) => {
  try {
    node.setErrors([])
    const client = useClient()
    const result = await client.formkit.action.mutate(formData)
    reflectResponse(result, { emit })
  } catch (e) {
    //TODO: reflect response/error?
    node.setErrors(
      [e.message],
    )
    return false;
  }
}
const onBatchInvoiceSubmit = async (formData, node) => {
  try {
    if (confirmMessage.value && !window.confirm(confirmMessage.value)){
      emit('close')
      return
    }

    const client = useClient()
    const carsStore = useSelectedCars();
    const result = await client.car.createBatchInvoice.mutate({
      ids: formData.carId ? [formData.carId] : carsStore.cars,
      ...formData
    })
    reflectResponse(result)
    emit('close')
  } catch (e) {
    node.setErrors(
      [e.message],
    )
    return false;
  }
}

const onDamageModalReset = () => {
  reset('damage-form')
}

const onCloseModal = () => {
  reset('damage-form')
  // emit('closeModal')
}

const carsList = computed(() => {
  const selectedStore = useSelectedCars()
  return selectedStore.cars
})

const initOptions = (options: object) => {
  try {
    return options
  } catch (e) {
    return []
  }
}

const initSchema = (schema: any) => {
  if (!schema) return []
  if (!Array.isArray(schema)) return initSchema([schema])
  return schema.map((item: any) => {
    if (item.children) {
      item.children = initSchema(item.children)
    }
    if (item.initOptions) {
      item.options = initOptions(item.initOptions)
      delete item.initOptions
    }
    return item
  })
}

const {confirmMessage, disabledSetting, applySettings} = useModalOptions({})

type UpdateState = 'loading' | 'idle'
const loading = ref<UpdateState>('idle')

const onFormChange = useDebounceFn(async (formData) => {
  const carsStore = useSelectedCars();
  loading.value = 'loading'

  //TODO: files need to be preloaded as tmp link
  //request oversize
  const form = JSON.parse(JSON.stringify(formData))
  if (form?.additionalInput?.files) {
    delete form.additionalInput.files
  }

  const {data, status, error} = await useDynamicFormSchema({
    code: code,
    formContext: {
      //TODO: fix
      carId: carId ? +carId : undefined,
      carIds: carsStore.cars,
    },
    //TODO: use ref for reactivity?
    formData: form,
  })

  if (error.value) {
    //TODO: handle error
    console.error(error)
    loading.value = 'idle'
    return
  }

  formSchema.value = initSchema(data.value.form)
  applySettings(data.value.settings)
  loading.value = 'idle'
}, 1000, { maxWait: 5000 })

const formkitData = reactive({
  t,
  loading,
  // vinCodeOptions,
  formData,
  disabledSetting,
  getClient,
  onCloseModal,
  onModalSubmit,
  onBatchInvoiceSubmit,
  onDamageModalReset,
  onFormChange,
  carsList,
  debug: (data: any) => JSON.stringify(data, null, 2),
})

const formSchema = ref([])

watch(() => form, () => {
  formSchema.value = initSchema(form)
  //TODO: if debug add to formkit schema
}, {immediate: true})

</script>

<template>
  <div class="requested-service-modal">
    <FormKitSchema
      :schema="formSchema"
      :data="formkitData"
      :library="library"
    />
  </div>
</template>

<style lang="scss" scoped>
.requested-service-modal {
  :deep(.two-columns) {
    display: flex;
    max-width: 100%;
    overflow: hidden;

    .indigo-ui-outer {
      margin-right: 20px;

      &:last-child {
        margin: 0;
      }
    }
  }

  :deep(.reply-inputs) {
    display: flex;

    > * {
      margin-right: 10px;
    }

    > :last-child {
      margin-right: 0;
    }

    .amount {
      width: 150px;
    }
  }

  :deep(.auto-columns-grid) {
    container-type: inline-size;
    display: grid;
    gap: 5px;
    grid-template-columns: repeat(auto-fit, minmax(298px, 1fr));


    @container (min-width: 300px) {
      grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
    }
    @container (min-width: 400px) {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }
    @container (min-width: 600px) {
      grid-template-columns: repeat(auto-fit, minmax(295px, 1fr));
    }
    @container (min-width: 1200px) {
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    }
  }

  :deep(.option-item-container) {
    display: grid;
    gap: 10px;
    width: 100%;
    .indigo-ui-outer {
      &[data-type="checkbox"] {
        margin-bottom: 0;
      }
    }
  }

  :deep(.section) {
    margin-bottom: 20px;
    .section-title{
      margin-bottom: 10px;
    }
  }

  :deep(.arrow-amount-to) {
    width: 16px;
    height: 14px;
    margin: 25px auto 5px;
    transform: rotate(90deg);
  }
}


</style>
